import { useEffect, useMemo, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { FiImage, FiSearch, FiX } from "react-icons/fi";
import { AlertMsg } from "../alert-msg";
import { LoadingItem } from "../loading";
import { appConstants } from "../../../redux/action-constants/inapp.constants";
import { ImageType, MediaPageProps } from "../../../types/app/mediaTypes";
import MediaSelectionPreview from "./MediaSelectionPreview";
import "./ImageDrawer.scss";

interface ImageDrawerProps {
  show: boolean;
  onClose: () => void;
  onSelect: (imageUrl: string) => void;
  currentImageUrl?: string | null;
  currentName: string;
  title: string;
  subtitle: string;
  tabLabel?: string;
  confirmLabel?: string;
  emptyStateText?: string;
  pageProps: Pick<
    MediaPageProps,
    "getImageGalleryAction" | "getImageGalleryRequest"
  >;
}

const PAGE_SIZE = 12;
const SEARCH_DELAY = 350;

const ImageDrawer = ({
  show,
  onClose,
  onSelect,
  currentImageUrl,
  currentName,
  title,
  subtitle,
  tabLabel = "Check library",
  confirmLabel = "Use image",
  emptyStateText = "No images found.",
  pageProps,
}: ImageDrawerProps) => {
  const [searchInputValue, setSearchInputValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [nextPageNumber, setNextPageNumber] = useState(1);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(
    currentImageUrl || null
  );
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [hasInitializedFetch, setHasInitializedFetch] = useState(false);
  const scrollContainerIdRef = useRef(
    `image-drawer-scroll-${Math.random().toString(36).slice(2)}`
  );

  const galleryResponse = pageProps?.getImageGalleryRequest?.request_data?.response;
  const libraryImages =
    pageProps?.getImageGalleryRequest?.galleryData ||
    pageProps?.getImageGalleryRequest?.request_data?.response?.results ||
    [];
  const galleryStatus = pageProps?.getImageGalleryRequest?.request_status;
  const galleryError = pageProps?.getImageGalleryRequest?.request_data?.error;
  const isGalleryLoading = pageProps?.getImageGalleryRequest?.is_request_processing;
  const totalNumberOfRecords = galleryResponse?.totalNumberOfRecords || 0;
  const hasMoreImages =
    libraryImages.length > 0 && libraryImages.length < totalNumberOfRecords;

  const loadLibraryImages = async (
    page: number,
    query: string,
    isNewBatch = false
  ) => {
    await pageProps.getImageGalleryAction(
      `page=${page}&size=${PAGE_SIZE}&fileName=${query}`,
      isNewBatch
    );
  };

  useEffect(() => {
    if (!show) {
      return;
    }

    setSelectedImageUrl(currentImageUrl || null);
    setSearchInputValue("");
    setSearchTerm("");
    setNextPageNumber(1);
    setIsFetchingMore(false);
    setHasInitializedFetch(false);
    pageProps.getImageGalleryAction("CLEAR", false);
  }, [show, currentImageUrl]);

  useEffect(() => {
    if (!show) {
      return;
    }

    const debounceTimer = window.setTimeout(() => {
      setSearchTerm(searchInputValue.trim());
    }, SEARCH_DELAY);

    return () => window.clearTimeout(debounceTimer);
  }, [show, searchInputValue]);

  useEffect(() => {
    if (!show) {
      return;
    }

    setNextPageNumber(1);
    setIsFetchingMore(false);
    setHasInitializedFetch(true);
    pageProps.getImageGalleryAction("CLEAR", false);
    loadLibraryImages(1, searchTerm, false);
  }, [show, searchTerm]);

  useEffect(() => {
    if (!isGalleryLoading) {
      setIsFetchingMore(false);
    }
  }, [isGalleryLoading]);

  const handleConfirmSelection = () => {
    if (!selectedImageUrl) {
      return;
    }

    onSelect(selectedImageUrl);
    onClose();
  };

  const loadMoreImages = async () => {
    if (
      isGalleryLoading ||
      isFetchingMore ||
      !hasInitializedFetch ||
      libraryImages.length >= totalNumberOfRecords
    ) {
      return;
    }

    const pageToLoad = nextPageNumber + 1;
    setIsFetchingMore(true);
    await loadLibraryImages(pageToLoad, searchTerm, true);
    setNextPageNumber(pageToLoad);
  };

  const selectedImage = useMemo(
    () =>
      libraryImages.find((image: ImageType) => image.imageUrl === selectedImageUrl),
    [libraryImages, selectedImageUrl]
  );

  if (!show) {
    return null;
  }

  return (
    <div className="image-drawer-overlay">
      <div className="image-drawer">
        <div className="image-drawer-header">
          <div>
            <h3>{title}</h3>
            <p>{subtitle}</p>
          </div>
          <button type="button" className="close-button" onClick={onClose}>
            <FiX size={22} />
          </button>
        </div>

        <div className="image-drawer-tabs">
          <button type="button" className="active">
            <FiImage size={16} />
            {tabLabel}
          </button>
        </div>

        <div className="image-drawer-content">
          <div className="image-drawer-toolbar">
            <div className="search-input-wrap">
              <FiSearch size={18} className="search-icon" />
              <input
                value={searchInputValue}
                onChange={(event) => setSearchInputValue(event.target.value)}
                placeholder="Search media library"
              />
            </div>
          </div>

          {galleryStatus === appConstants.GET_IMAGE_GALLERY_FAILURE && (
            <AlertMsg type="error" message={galleryError} />
          )}

          {isGalleryLoading && libraryImages.length === 0 ? (
            <div className="image-drawer-loading">
              <LoadingItem />
            </div>
          ) : libraryImages.length === 0 ? (
            <div className="image-drawer-empty-state">
              <FiImage size={40} />
              <p>{emptyStateText}</p>
            </div>
          ) : (
            <div
              id={scrollContainerIdRef.current}
              className="image-drawer-scroll-container hide-scrollbar"
            >
              <InfiniteScroll
                dataLength={libraryImages.length}
                next={loadMoreImages}
                hasMore={hasMoreImages}
                loader={
                  <div className="image-drawer-grid-loader">
                    <LoadingItem />
                  </div>
                }
                scrollableTarget={scrollContainerIdRef.current}
              >
                <div className="image-drawer-grid">
                  {libraryImages.map((image: ImageType) => (
                    <button
                      key={image.id}
                      type="button"
                      className={`image-drawer-card ${
                        selectedImageUrl === image.imageUrl ? "selected" : ""
                      }`}
                      onClick={() => setSelectedImageUrl(image.imageUrl)}
                    >
                      <MediaSelectionPreview
                        imageUrl={image.imageUrl}
                        name={image.name || currentName || "Selected image"}
                        className="image-drawer-card-preview"
                      />
                      <span>{image.name || "Untitled image"}</span>
                    </button>
                  ))}
                </div>
              </InfiniteScroll>
            </div>
          )}

          <div className="image-drawer-footer">
            <div className="selected-preview-text">
              {selectedImage ? selectedImage.name || "Selected image" : "No image selected"}
            </div>
            <div className="image-drawer-actions">
              <button type="button" className="image-drawer-ghost-btn" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="image-drawer-primary-btn"
                onClick={handleConfirmSelection}
                disabled={!selectedImageUrl}
              >
                {confirmLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageDrawer;
